import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { createBusiness } from '../../api/businessesReader'
import { getWalletSummary } from '../../api/wallet'
import type { BusinessProfile, CreateBusinessPayload } from '../../types/businesses'
import {
  EMPTY_CREATE_BUSINESS_DRAFT,
  getCreateBusinessIntent,
  setCreateBusinessIntent,
  clearCreateBusinessIntent,
  type CreateBusinessDraft,
} from '../../lib/pagesOnboarding'

function normalizeWebsite(value: string): string {
  const raw = value.trim()
  if (!raw) return ''
  if (raw.startsWith('http://') || raw.startsWith('https://')) return raw
  return `https://${raw}`
}

function extractApiErrorMessage(error: any, fallback: string): string {
  const detail = error?.response?.data?.detail
  if (typeof detail === 'string' && detail.trim()) return detail
  const data = error?.response?.data
  if (data && typeof data === 'object') {
    const firstKey = Object.keys(data)[0]
    if (firstKey) {
      const value = (data as any)[firstKey]
      if (Array.isArray(value) && value.length > 0) return String(value[0])
      if (typeof value === 'string') return value
    }
  }
  if (typeof error?.message === 'string' && error.message.trim()) return error.message
  return fallback
}

type Props = {
  open: boolean
  originPath: string
  onClose: () => void
  onCreated: (business: BusinessProfile) => void
}

export default function CreateBusinessModal({ open, originPath, onClose, onCreated }: Props) {
  const navigate = useNavigate()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [form, setForm] = useState<CreateBusinessDraft>(EMPTY_CREATE_BUSINESS_DRAFT)
  const [feeAcknowledged, setFeeAcknowledged] = useState(false)
  const [draftRestored, setDraftRestored] = useState(false)
  const [insufficientFunds, setInsufficientFunds] = useState(false)
  const [walletBalance, setWalletBalance] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    const intent = getCreateBusinessIntent()
    if (intent?.draft) {
      const restored = { ...EMPTY_CREATE_BUSINESS_DRAFT, ...intent.draft }
      setForm(restored)
      setDraftRestored(Object.values(restored).some((v) => String(v || '').trim().length > 0))
    } else {
      setForm(EMPTY_CREATE_BUSINESS_DRAFT)
      setDraftRestored(false)
    }
    setError(null)
    setInsufficientFunds(false)
    setFeeAcknowledged(false)
  }, [open])

  useEffect(() => {
    if (!open) return
    const t = window.setTimeout(() => {
      setCreateBusinessIntent({ originPath, draft: form })
    }, 300)
    return () => window.clearTimeout(t)
  }, [open, form, originPath])

  useEffect(() => {
    if (!open) return
    let cancelled = false
    getWalletSummary()
      .then((data) => {
        if (cancelled) return
        const bal = data?.summary?.pixe_balance
        setWalletBalance(typeof bal === 'string' ? bal : null)
      })
      .catch(() => {
        if (!cancelled) setWalletBalance(null)
      })
    return () => {
      cancelled = true
    }
  }, [open])

  const canSubmit = useMemo(() => {
    return form.name.trim().length > 0 && form.category.trim().length > 0 && feeAcknowledged
  }, [form.name, form.category, feeAcknowledged])

  const isInsufficientBalanceError = (e: any): boolean => {
    const code = e?.response?.data?.code
    const detail = String(e?.response?.data?.detail || '')
    const message = String(e?.message || '')
    if (code === 'INSUFFICIENT_BALANCE') return true
    return /insufficient\s*pixe/i.test(detail) || /insufficient\s*pixe/i.test(message)
  }

  const onCreate = async () => {
    if (!canSubmit || saving) return
    setSaving(true)
    setError(null)
    setInsufficientFunds(false)
    try {
      const payload: CreateBusinessPayload = {
        name: form.name.trim(),
        category: form.category.trim(),
        description: form.description.trim(),
        phone: form.phone.trim(),
        website: normalizeWebsite(form.website),
        address: form.address.trim(),
      }
      const created = await createBusiness(payload)
      clearCreateBusinessIntent()
      onCreated(created)
    } catch (e: any) {
      if (isInsufficientBalanceError(e)) {
        setInsufficientFunds(true)
        setError('Insufficient PIXE balance.')
      } else {
        setError(extractApiErrorMessage(e, 'Failed to create page'))
      }
    } finally {
      setSaving(false)
    }
  }

  const onResetDraft = () => {
    clearCreateBusinessIntent()
    setForm(EMPTY_CREATE_BUSINESS_DRAFT)
    setDraftRestored(false)
    setError(null)
    setInsufficientFunds(false)
    setFeeAcknowledged(false)
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/65" onClick={() => !saving && onClose()} />
      <div className="absolute inset-x-4 top-16 mx-auto max-w-xl rounded-2xl border border-white/10 bg-neutral-950 p-4">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-extrabold">Create Business Page</h2>
          <button type="button" className="text-sm text-white/70 hover:text-white" onClick={() => !saving && onClose()}>
            Close
          </button>
        </div>

        <div className="mb-3 rounded-xl border border-white/10 bg-white/5 p-3">
          <div className="text-sm font-semibold">One-time setup fee: 50 PIXE</div>
          <div className="mt-1 text-xs text-white/70">This prevents spam and fake listings.</div>
          {walletBalance ? <div className="mt-1 text-xs text-white/65">Your balance: {walletBalance} PIXE</div> : null}
          <label className="mt-2 inline-flex items-center gap-2 text-sm text-white/85">
            <input
              type="checkbox"
              checked={feeAcknowledged}
              onChange={(e) => setFeeAcknowledged(e.target.checked)}
              className="h-4 w-4 rounded border-white/30 bg-black/20"
            />
            I understand the 50 PIXE setup fee.
          </label>
        </div>

        {draftRestored ? (
          <div className="mb-2 flex items-center justify-between text-xs text-white/60">
            <span>Draft restored from earlier.</span>
            <button type="button" className="text-white/75 hover:underline" onClick={onResetDraft}>
              Reset draft
            </button>
          </div>
        ) : null}

        <div className="grid gap-3">
          <input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder="Business name" className="rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand" />
          <input value={form.category} onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))} placeholder="Category" className="rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand" />
          <textarea value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} placeholder="Description" rows={3} className="rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand" />
          <input value={form.phone} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} placeholder="Phone" className="rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand" />
          <input value={form.website} onChange={(e) => setForm((p) => ({ ...p, website: e.target.value }))} placeholder="Website" className="rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand" />
          <input value={form.address} onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))} placeholder="Address" className="rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand" />
        </div>

        {insufficientFunds ? (
          <div className="mt-3 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3">
            <div className="text-sm font-semibold text-amber-100">You need 50 PIXE to create a business page.</div>
            <div className="mt-2 flex items-center gap-2">
              <button
                type="button"
                onClick={() => navigate('/settings/wallet')}
                className="rounded-lg bg-brand px-3 py-2 text-xs font-semibold text-white hover:bg-brand-600"
              >
                Go to Wallet
              </button>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-xs hover:bg-white/10"
              >
                Close
              </button>
            </div>
          </div>
        ) : null}

        {error ? <div className="mt-3 text-sm text-red-300">{error}</div> : null}

        <div className="mt-4 flex items-center justify-end gap-2">
          <button type="button" onClick={() => !saving && onClose()} className="rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm hover:bg-white/10">
            Cancel
          </button>
          <button
            type="button"
            disabled={!canSubmit || saving}
            onClick={onCreate}
            className={`rounded-lg px-3 py-2 text-sm font-semibold ${!canSubmit || saving ? 'bg-white/10 text-white/50 cursor-not-allowed' : 'bg-brand text-white hover:bg-brand-600'}`}
          >
            {saving ? 'Creating...' : 'Create (50 PIXE)'}
          </button>
        </div>
      </div>
    </div>
  )
}
